import { ABSENCE_TYPES_COMBOBOX_QUERY } from '@/modules/timesheets/queries'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ResultOf } from 'gql.tada'
import type { ComboboxCommonProps, ComboboxItem } from './combobox'
import { Combobox } from './combobox'

export type ComboboxAbsenceType = ResultOf<
  typeof ABSENCE_TYPES_COMBOBOX_QUERY
>['allAbsenceTypes'][number]

interface CommonProps extends ComboboxCommonProps {
  hideItem?(item: ComboboxAbsenceType, idx: number): boolean
}

interface SingleProps extends CommonProps {
  mode: 'single'
  value: string
  onChange(value: string): void
}

interface MultiProps extends CommonProps {
  mode: 'multiple'
  value: string[]
  onChange(value: string[]): void
}

type Props = SingleProps | MultiProps

export function AbsenceTypeCombobox({
  mode,
  value,

  placeholder,
  searchPlaceholder,
  noResultsText,

  hideItem,

  onChange,

  ...props
}: Props) {
  const translations = useTranslate({
    single: {
      placeholder: 'components.combobox.absence-type.placeholder',
      searchPlaceholder: 'components.combobox.absence-type.search-placeholder',
      noResults: 'components.combobox.absence-type.no-results',
    },
    multiple: {
      placeholder: 'components.multi-combobox.absence-type.placeholder',
      searchPlaceholder:
        'components.multi-combobox.absence-type.search-placeholder',
      noResults: 'components.multi-combobox.absence-type.no-results',
    },
  })

  const { data: absenceTypesData } = useQuery(ABSENCE_TYPES_COMBOBOX_QUERY)

  const absenceTypeItems: ComboboxItem[] = (() => {
    const absenceTypes = absenceTypesData?.allAbsenceTypes ?? []

    const items = absenceTypes.reduce<ComboboxItem[]>((acc, type, idx) => {
      if (!!hideItem?.(type, idx)) return acc

      acc.push({
        value: type.id,
        label: (
          <span className="inline-flex items-center">
            <span
              className="inline-block mr-2 size-4 rounded-sm -translate-y-px"
              style={{ backgroundColor: type.color }}
            />

            {type.name}
          </span>
        ),
        filterValue: type.name,
      })

      return acc
    }, [])

    return items
  })()

  if (mode === 'single')
    return (
      <Combobox
        {...props}
        mode="single"
        value={value}
        items={absenceTypeItems}
        clearable
        placeholder={placeholder ?? translations.single.placeholder}
        searchPlaceholder={
          searchPlaceholder ?? translations.single.searchPlaceholder
        }
        noResultsText={noResultsText ?? translations.single.noResults}
        onChange={onChange}
      />
    )

  return (
    <Combobox
      {...props}
      mode="multiple"
      value={value}
      items={absenceTypeItems}
      placeholder={placeholder ?? translations.multiple.placeholder}
      searchPlaceholder={
        searchPlaceholder ?? translations.multiple.searchPlaceholder
      }
      noResultsText={noResultsText ?? translations.multiple.noResults}
      onChange={onChange}
    />
  )
}
